import { useMemo } from 'react'
import { Atrium, PenIcon, TrashIcon } from '@ofself/shell'
import { qh } from './qh.js'

/* THE ATRIUM: every dictionary at once, as a periodic table (owner call
   2026-09-30, after nucleus's Home: "a periodic table kind of style within a
   dot grid"). The table itself is the kit's <Atrium> (owner call 2026-10-01:
   the dictionary's table is ONE SOURCE for every app; ShellKit's Atrium on
   the Mac, the same on the web): EVERY WORD alone as element 00, then the
   DICTIONARIES in your order, then the plus. This file only says what a
   dictionary is and what its verbs do.

   A DICTIONARY IS A LENS, NOT A WALL (owner, 2026-09-20): a dictionary is a
   tag the words already carry, so a word stands in as many dictionaries as it
   has tags. Every word is the list with no lens on it. Deleting a dictionary
   takes the tag off its words; the words stay. An ANTI-DICTIONARY is one of
   them, worn with a red ANTI badge (owner call 2026-10-01: a badge, not a
   section): its words inherit the anti-word stance. A right-click is the
   Finder's: open, rename (in place), duplicate, set as anti-dictionary,
   export, delete. Words carried here from the list land in the dictionary
   (the kit's items drag); Every word and the dictionary in force refuse them.

   IN YOUR ORDER (owner ask 2026-10-03): `shelves` arrive in the order you
   dragged them into (the shell's AtriumOrder; before that, the order they were
   founded); the kit numbers each by its place there. */

const MASTER = ''
const counted = (n) => `${n} word${n === 1 ? '' : 's'}`

/* words: the identity's own; shelves: [{ tag, anti, id, words }], every
   dictionary, its node's or its tag's; lens: the tag in force. */
export default function Shelves({ words, shelves, lens = '', onOpen, onMint, onDelete, onRename, onSetAnti, onDuplicate, onExport, onDropWords, onReorder }) {
  const rooms = useMemo(() => {
    const master = words.filter((w) => w.stance !== 'avoided').length
    return [
      { id: MASTER, name: 'every word', master: true, count: master, meta: counted(master),
        help: 'Every word you hold, whichever dictionary. Press it to read the list with no lens on it.' },
      ...shelves.map((s, i) => {
        // an anti-dictionary counts its words; a dictionary the words it holds
        const n = (s.anti ? s.words : s.words.filter((w) => w.stance !== 'avoided')).length
        return {
          id: s.tag, name: s.tag, seat: i + 1, count: n, meta: counted(n), badge: s.anti ? 'anti' : undefined, anti: s.anti,
          help: s.anti
            ? 'An anti-dictionary: its words are anti-words, caught wherever you write them. Press it to read them; right-click for rename, duplicate, set as dictionary, export and delete.'
            : `A dictionary, a lens over the words tagged “${s.tag}”. Press it to read the list through it; drag it into your own order; right-click for rename, duplicate, set as anti-dictionary, export and delete.`,
        }
      }),
    ]
  }, [words, shelves])

  // THE FINDER'S VERBS (owner call 2026-10-01)
  const menu = (t, { rename }) => [
    { label: 'Open', onSelect: () => onOpen(t.id) },
    ...(rename ? [{ icon: <PenIcon size={16} />, label: 'Rename…', onSelect: rename }] : []),
    ...(onDuplicate ? [{ label: 'Duplicate', onSelect: () => onDuplicate(t.id) }] : []),
    ...(onSetAnti ? [t.anti
      ? { label: 'Set as dictionary', onSelect: () => onSetAnti(t.id, false) }
      : { label: 'Set as anti-dictionary', onSelect: () => onSetAnti(t.id, true) }] : []),
    ...(onExport ? [{ label: 'Export', items: [
      { label: 'As JSON', onSelect: () => onExport(t.id, 'json') },
      { label: 'As Markdown files', onSelect: () => onExport(t.id, 'md') },
    ] }] : []),
    { separator: true },
    { icon: <TrashIcon size={16} />, label: 'Delete…', danger: true, onSelect: () => {
      const n = t.count
      if (!window.confirm(`delete the ${t.anti ? 'anti-dictionary' : 'dictionary'} “${t.name}”? The tag comes off its ${n} word${n === 1 ? '' : 's'}; the word${n === 1 ? ' stays' : 's stay'} in the dictionary.`)) return
      onDelete(t.id)
    } },
  ]

  return (
    <div className="dt-atrium" {...qh('All dictionaries', 'Every dictionary at once, as a periodic table. A dictionary is a tag, so a word stands in every dictionary it is tagged with; an anti-dictionary wears a red ANTI badge and its words are anti-words. Press an element to read the list through it; right-click for the rest; the plus founds one.')}>
      <Atrium title="Dictionaries" masterTitle="Every word" rooms={rooms} activeId={lens}
        onOpen={(t) => onOpen(t.id)} onMint={onMint} mintLabel="New dictionary"
        mintHelp="Founds a dictionary by name. It is a tag: tag a word with it and the word stands here. Right-click it later to make it an anti-dictionary."
        onRename={onRename} menu={menu}
        // the dictionaries' tags in their new order (the master is the kit's, not a tag)
        onReorder={onReorder && ((ids) => onReorder(ids.filter((id) => id !== MASTER)))}
        onDropItems={onDropWords && ((tag, ids) => onDropWords(tag, ids))} />
    </div>
  )
}
